import { Capacitor, registerPlugin } from "@capacitor/core";
import JSZip from "jszip";
import { normalizeDesign, type StorageDesign } from "../domain/design";
import type { SignedEntitlement } from "../domain/entitlement";
import { formatJpy, hakofitDataCreationFeeJpy } from "../domain/pricing";
import { createStl } from "../geometry/stl";

export type ProjectZipAccess = "web-entitlement" | "ios-unlimited" | "web-preview";

type ProjectZipOptions = {
  access?: ProjectZipAccess;
  entitlement?: SignedEntitlement;
};

type HakofitFilesPlugin = {
  shareZip(options: { base64: string; filename: string }): Promise<void>;
};

const HakofitFiles = registerPlugin<HakofitFilesPlugin>("HakofitFiles");

export function downloadText(filename: string, content: string, type = "application/json"): void {
  downloadBlob(filename, new Blob([content], { type }));
}

export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function createDesignJson(design: StorageDesign): string {
  return `${JSON.stringify(normalizeDesign(design), null, 2)}\n`;
}

export async function downloadProjectZip(
  design: StorageDesign,
  options: ProjectZipOptions = {}
): Promise<void> {
  const blob = await createProjectZipBlob(design, options);

  if (Capacitor.isNativePlatform()) {
    await shareNativeBlob("hakofit-output.zip", blob);
    return;
  }

  downloadBlob("hakofit-output.zip", blob);
}

export async function createProjectZipBlob(
  design: StorageDesign,
  options: ProjectZipOptions = {}
): Promise<Blob> {
  const zip = new JSZip();
  const stl = createStl(design);
  const access = getProjectZipAccess(options);

  zip.file("model.stl", stl);
  zip.file("design.json", createDesignJson(design));

  if (options.entitlement) {
    zip.file("hakofit-key.json", `${JSON.stringify(options.entitlement, null, 2)}\n`);
  }

  zip.file("readme-ja.txt", createJapaneseReadme(access, options.entitlement));
  zip.file("readme-en.txt", createEnglishReadme(access, options.entitlement));

  return zip.generateAsync({ type: "blob" });
}

function createJapaneseReadme(access: ProjectZipAccess, entitlement?: SignedEntitlement): string {
  const isIosUnlimited = access === "ios-unlimited";

  return `${[
    "ハコフィット 出力データ",
    "",
    "このZIPには、ハコフィットで作成した3Dプリント用データ一式が入っています。",
    "",
    "ファイル内容",
    "- model.stl: 外部プリントサービスや3Dプリンターへ渡す3Dモデルです。",
    "- design.json: この設計内容を復元するための設計データです。",
    isIosUnlimited
      ? "- hakofit-key.json: iOS版の買い切りアップグレード出力では同梱されません。"
      : entitlement
      ? "- hakofit-key.json: 支払い済みであることを確認するための再出力キーです。"
      : "- hakofit-key.json: 支払い後のZIPに同梱されます。",
    "- readme-ja.txt: この日本語の説明です。",
    "- readme-en.txt: 英語の説明です。",
    "",
    "再出力について",
    isIosUnlimited
      ? "- iOS版でアップグレード済みの場合、アプリ内で何度でも出力できます。"
      : entitlement
      ? `- キー有効期限: ${entitlement.expires_at}`
      : "- 支払い後に発行されるキーは14日間有効です。",
    isIosUnlimited
      ? "- design.jsonをアプリで読み込むことで、同じ設計を復元できます。"
      : "- 有効期限内は、同じdesign.jsonとhakofit-key.jsonをサイトで読み込むことで、同じ設計データを再出力できます。",
    isIosUnlimited
      ? "- 寸法、仕切り、取手などを変更した別設計も、アップグレード済みのiOS版では追加決済なしで出力できます。"
      : "- 寸法、仕切り、取手などを変更した別設計の出力には、新しい決済が必要です。",
    isIosUnlimited
      ? "- design.jsonを紛失した場合、自動復元はできません。"
      : "- design.jsonまたはhakofit-key.jsonを紛失した場合、自動復元はできません。",
    "",
    "外部プリントサービスへ入稿する場合",
    "- アップロードするファイルはmodel.stlです。ZIPファイル全体ではありません。",
    "- 造形費、送料、素材、納期、造形可否はアップロード先のサービス側で確認してください。",
    "",
    isIosUnlimited
      ? "ハコフィット iOS版: アプリ内アップグレードにより出力"
      : `ハコフィット データ出力料: ${formatJpy(hakofitDataCreationFeeJpy)}`
  ].join("\n")}\n`;
}

function createEnglishReadme(access: ProjectZipAccess, entitlement?: SignedEntitlement): string {
  const isIosUnlimited = access === "ios-unlimited";

  return `${[
    "Hakofit Output Data",
    "",
    "This ZIP contains the 3D print data package created with Hakofit.",
    "",
    "Files",
    "- model.stl: The 3D model to upload to a print service or use with your 3D printer.",
    "- design.json: The design data used to restore this exact design.",
    isIosUnlimited
      ? "- hakofit-key.json: Not included for unlimited iOS upgrade exports."
      : entitlement
      ? "- hakofit-key.json: The re-export key used to confirm paid export status."
      : "- hakofit-key.json: Included in the ZIP after payment.",
    "- readme-ja.txt: Japanese instructions.",
    "- readme-en.txt: These English instructions.",
    "",
    "Re-export",
    isIosUnlimited
      ? "- If the iOS app is upgraded, you can export as many designs as needed in the app."
      : entitlement
      ? `- Key expiration: ${entitlement.expires_at}`
      : "- The key issued after payment is valid for 14 days.",
    isIosUnlimited
      ? "- You can restore the same design in the app by loading design.json."
      : "- While the key is valid, you can load the same design.json and hakofit-key.json on the site to re-export the same design data.",
    isIosUnlimited
      ? "- Changed dimensions, dividers, handles, and other settings can also be exported without another iOS purchase."
      : "- Exporting a different design after changing dimensions, dividers, handles, or other settings requires a new payment.",
    isIosUnlimited
      ? "- If you lose design.json, automatic restoration is not available."
      : "- If you lose design.json or hakofit-key.json, automatic restoration is not available.",
    "",
    "Using an external print service",
    "- Upload model.stl, not the whole ZIP file.",
    "- Manufacturing cost, shipping, material, delivery time, and manufacturability are determined by the upload destination.",
    "",
    isIosUnlimited
      ? "Hakofit iOS: exported through the in-app upgrade."
      : `Hakofit data export fee: ${hakofitDataCreationFeeJpy.toLocaleString("en-US")} JPY`
  ].join("\n")}\n`;
}

function getProjectZipAccess(options: ProjectZipOptions): ProjectZipAccess {
  if (options.access) {
    return options.access;
  }

  return options.entitlement ? "web-entitlement" : "web-preview";
}

async function shareNativeBlob(filename: string, blob: Blob): Promise<void> {
  const data = await blobToBase64(blob);

  await HakofitFiles.shareZip({ filename, base64: data });
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;

      if (typeof result !== "string") {
        reject(new Error("Could not encode the ZIP file."));
        return;
      }

      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Could not read the ZIP file."));
    reader.readAsDataURL(blob);
  });
}
